import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@emotion/react';
import { useContext } from 'react';
import { AppTheme } from 'theme';

import {
  ActiveGames_activeGames,
  ActiveGamesByDifficultyAndScene_activeGamesByDifficultyAndScene,
  PlayerRole,
} from '../../../../graphql/types';
import { useOnlineUserStates } from '../../hooks/useOnlineUserStates';

import GameTableRow from './GameTableRow';

type ActiveGame =
  | ActiveGames_activeGames
  | ActiveGamesByDifficultyAndScene_activeGamesByDifficultyAndScene;
interface Props {
  games: ActiveGame[];
  className?: string;
  isSelectedScene?: boolean; // IS_EDU - is used on EDU layout only for now
}

const GameTable = ({ games, className, isSelectedScene }: Props) => {
  const [t] = useTranslation();

  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;
  const LOCIZE_SUFFIX_KEY = IS_EDU ? '.edu' : '';

  const opponentIds = getOpponentIds(games);
  // load opponent states and write to cache. This will be used by GameRow/PlayerView component
  useOnlineUserStates(opponentIds);
  return (
    <Content className={className}>
      <thead>
        <tr>
          <HeaderCell>
            {t('lobby.gamelist.dateAndTime', 'DATUM A ČAS')}
          </HeaderCell>
          {!isSelectedScene && (
            <>
              <HeaderCell>{t('lobby.gamelist.scene', 'DĚJIŠTĚ')}</HeaderCell>
              <HeaderCell>
                {t('lobby.gamelist.gameDifficulty', 'ÚROVEŇ HRY')}
              </HeaderCell>
            </>
          )}
          <HeaderCell>
            {t('lobby.gamelist.yourRole' + LOCIZE_SUFFIX_KEY, 'VAŠE ROLE')}
          </HeaderCell>
          <HeaderCell>
            {t('lobby.gamelist.yourEnemy' + LOCIZE_SUFFIX_KEY, 'VÁŠ PROTIVNÍK')}
          </HeaderCell>
          <HeaderCell />
        </tr>
      </thead>
      <tbody>
        {games.map((game) => (
          <GameTableRow
            key={game.id}
            game={game}
            isSelectedScene={isSelectedScene}
          />
        ))}
      </tbody>
    </Content>
  );
};

export default GameTable;

const getOpponentIds = (games: ActiveGame[]) => {
  return games
    .map((game) => {
      const opponent =
        game.myRole === PlayerRole.EMPLOYEE ? game.hacker : game.employee;
      return opponent.user?.id;
    })
    .filter(isNotNull)
    .filter((id, i, arr) => arr.indexOf(id) === i);
};

function isNotNull<T>(value: T | null | undefined): value is T {
  return value != null;
}

const Content = styled.table`
  width: 100%;
  max-width: 1000px;
  border-spacing: 0px 12px;
  margin-left: 1em;
  margin-right: 1em;
  z-index: 1;
`;

const HeaderCell = styled.th`
  text-align: left;
  font-size: 12px;
  font-weight: normal;
  color: #615e8b;
  letter-spacing: 0;
  line-height: 22px;
  padding-right: 1em;
`;
